import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import moment, { Moment } from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from 'src/store/RootReducer';
import { AppActions } from 'src/store/AppReducer';
import { getRolePermissions } from 'src/services/authService';
import { validateMultiRolesAndPermissions } from 'src/utils/validate';
import { getAllDepartments, getAllEmployees } from 'src/services/employeeService';
import { getAllProjects, getAllProjectsInternal } from 'src/services/projectDetailService';
import { getProjectsManagedBy, getTeams } from 'src/services/projectService';
import {
  attendanceApiError,
  AttendanceApiError,
  attendanceErrorMessage,
  DailyAttendance,
  getAttendanceGrid,
  getMyAttendance,
  importAttendance
} from 'src/services/lmsAttendanceService';
import SnackBar from 'src/components/SnackBar/SnackBar';
import DownloadReportDialog from './DownloadReportDialog';
import ImportErrorDialog from './ImportErrorDialog';
import EmployeeMapping from './EmployeeMapping';
import SearchableSelect from './SearchableSelect';
import WeeksTab from 'src/content/timeEntry/components/weeks-tab';
import {
  DirectoryEmployee,
  ProjectCatalogItem,
  TeamMemberRef,
  employeesForTeam,
  teamMembersFromRoles,
  toCatalogProject,
  toDirectoryEmployee
} from './attendanceOptions';

type EmployeeRow = DirectoryEmployee;

type ProjectOption = {
  code: string;
  id: string;
  name: string;
};

const RM_PM_DEPARTMENTS = ['BA', 'Development', 'QA', 'PM'];

const NO_PM_PROJECTS_MESSAGE = 'There are no projects within the selected period.';

type PmProjectStatus = 'idle' | 'loading' | 'ready' | 'none' | 'error';

const startOfWeek = (date: Date | Moment) =>
  moment(date).startOf('isoWeek').startOf('day');

type AttendanceWeek = {
  weekNum: number;
  year: number;
  week: string;
  weekStartDate: Date;
  weekEndDate: Date;
};

const buildAttendanceWeeks = (startYear: number, endYear: number): AttendanceWeek[] => {
  const weeks: AttendanceWeek[] = [];
  let weekNumber = 1;
  const date = new Date(startYear, 0, 1);
  const firstDay = date.getDay();
  if (firstDay !== 1) {
    date.setDate(date.getDate() + (8 - firstDay));
  }

  while (date.getFullYear() <= endYear) {
    const startDate = new Date(date);
    const endDate = new Date(date);
    endDate.setDate(date.getDate() + 6);
    const startLabel = `${startDate.toLocaleString('default', { month: 'short' })} ${startDate.getDate()}`;
    const endLabel = `${endDate.toLocaleString('default', { month: 'short' })} ${endDate.getDate()}`;
    weeks.push({
      weekNum: weekNumber,
      year: startDate.getFullYear(),
      week: `${startLabel} - ${endLabel}`,
      weekStartDate: startDate,
      weekEndDate: endDate
    });
    date.setDate(date.getDate() + 7);
    weekNumber += 1;
  }

  return weeks;
};

const weekRangeFor = (weekNum: number) => ({
  from: weekNum - 1,
  to: weekNum + 2
});

const isTruthy = (value: unknown) => value === true || value === 'true';

const matchesDepartment = (employee: EmployeeRow, department: string) =>
  department === 'all' ||
  employee.department.toLowerCase() === department.toLowerCase();

const attendanceCellLabel = (day?: DailyAttendance) => {
  if (!day) {
    return '-';
  }
  const inTime = day.inTime ? `${day.inTime}${day.late ? ' Late' : ''}` : '';
  const status =
    day.onLeave || day.halfDay
      ? day.display || (day.halfDay ? 'Half-day' : day.leaveType || 'Leave')
      : day.holiday
      ? holidayTypeLabel(day)
      : '';
  if (inTime && status) {
    return `${inTime} ${status}`;
  }
  if (inTime) {
    return inTime;
  }
  if (status) {
    return status;
  }
  return day.display || '-';
};

const leaveText = (day?: DailyAttendance) =>
  `${day?.leaveType || ''} ${day?.display || ''}`.toLowerCase();

const leaveBadge = (day?: DailyAttendance) => {
  const text = leaveText(day);
  if (text.includes('no pay')) {
    return { label: 'No Pay Leave', border: '#E53935', text: '#E53935' };
  }
  if (text.includes('casual')) {
    return { label: 'Casual-Leave', border: '#F3C56A', text: '#E09A12' };
  }
  if (text.includes('annual')) {
    return { label: 'Annual-Leave', border: '#F3C56A', text: '#E09A12' };
  }
  return null;
};

const dayHasRecord = (day?: DailyAttendance) =>
  Boolean(day?.inTime || day?.onLeave || day?.halfDay || day?.leaveType || day?.holiday);

const holidayTypeLabel = (day?: DailyAttendance) => (day?.holidayType || '').trim() || 'Holiday';

const holidayTooltip = (day?: DailyAttendance) => {
  const session = (day?.holidaySession || '').trim();
  return session ? `${holidayTypeLabel(day)} - ${session}` : holidayTypeLabel(day);
};

const holidayTypeColor = (type?: string | null) => {
  const value = (type || '').toLowerCase();
  if (value.includes('mercantile')) {
    return '#ED1C24';
  }
  if (value.includes('bank')) {
    return '#1565C0';
  }
  if (value.includes('company')) {
    return '#00875A';
  }
  if (value.includes('public')) {
    return '#E09A12';
  }
  return '#6267FD';
};

const OverflowText = ({
  text,
  color,
  badge,
  tooltip
}: {
  text: string;
  color: string;
  badge?: boolean;
  /** Always shown on hover. Without it, the full text is shown only when it is cut off. */
  tooltip?: string;
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) {
      return;
    }
    setOverflowing(element.scrollWidth > element.clientWidth + 1);
  }, [text]);

  const label = (
    <Typography
      ref={textRef}
      component="span"
      variant="body2"
      sx={{
        display: 'block',
        width: '100%',
        maxWidth: 120,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.4,
        ...(badge
          ? {
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              border: '1px solid',
              borderColor: color,
              backgroundColor: `${color}24`
            }
          : {})
      }}
    >
      {text}
    </Typography>
  );

  if (!tooltip && !overflowing) {
    return label;
  }

  return (
    <Tooltip title={tooltip || text} placement="top">
      {label}
    </Tooltip>
  );
};

const isShortLeave = (day?: DailyAttendance) => leaveText(day).includes('short');

const sessionText = (day?: DailyAttendance) => (day?.session || '').trim();

const withSession = (label: string, day?: DailyAttendance) => {
  const session = sessionText(day);
  if (!session || label.toLowerCase().includes(session.toLowerCase())) {
    return label;
  }
  return `${label} (${session})`;
};

const shortLeaveLabel = (day?: DailyAttendance) => withSession('Short Leave', day);

const halfDayBase = (day?: DailyAttendance) => day?.display || 'Half-day';

const Attendance = () => {
  const dispatch = useDispatch();
  const appUser = useSelector((state: RootStateType) => state.appReducer.user);
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const roles = String(storedUser?.Roles || appUser?.Roles || '')
    .split(',')
    .map((role) => role.trim())
    .filter(Boolean);
  const isSuperAdmin = roles.includes('Super User');
  const isHr = roles.some((role) => /hr/i.test(role));
  const isPmOrRm =
    isTruthy(appUser?.IsReportingManager) || isTruthy(appUser?.IsProjectManager);
  const isRmOrPm =
    isPmOrRm ||
    roles.includes('Project Manager') ||
    roles.includes('Reporting Manager');
  const isProjectManager =
    isTruthy(storedUser?.IsProjectManager) ||
    isTruthy(appUser?.IsProjectManager) ||
    roles.some((role) => role.toLowerCase() === 'project manager');
  const isProjectLead = roles.some((role) => role.toLowerCase() === 'project lead');
  const isProjectManagerOrLead = isProjectManager || isProjectLead;
  const currentEmployeeId = String(storedUser?.EmployeeId || appUser?.EmployeeId || '').trim();
  const [canViewEmployeeAttendance, setCanViewEmployeeAttendance] = useState(false);
  const usePmProjects = isProjectManager && canViewEmployeeAttendance;
  const [canImportAttendance, setCanImportAttendance] = useState(false);
  const [canDownloadAttendance, setCanDownloadAttendance] = useState(false);
  const [canViewEmployeeMapping, setCanViewEmployeeMapping] = useState(false);
  const [canAddEmployeeMapping, setCanAddEmployeeMapping] = useState(false);
  const [canEditEmployeeMapping, setCanEditEmployeeMapping] = useState(false);
  const [canRemoveEmployeeMapping, setCanRemoveEmployeeMapping] = useState(false);
  const showProjectFilter =
    canViewEmployeeAttendance && (isSuperAdmin || isProjectManager || isProjectLead);
  const showDepartmentFilter = canViewEmployeeAttendance && (isSuperAdmin || isHr || isRmOrPm);
  const showEmployeeFilter = canViewEmployeeAttendance;
  const showEmployeeMapping =
    canViewEmployeeMapping ||
    canAddEmployeeMapping ||
    canEditEmployeeMapping ||
    canRemoveEmployeeMapping;

  const [tab, setTab] = useState<'mine' | 'employees' | 'mapping'>('mine');
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberRef[]>([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const attendanceWeeks = useMemo(() => {
    const year = new Date().getFullYear();
    return buildAttendanceWeeks(year - 1, year + 1);
  }, []);
  const selectedAttendanceWeek = useMemo(
    () =>
      attendanceWeeks.find((week) =>
        moment(week.weekStartDate).startOf('day').isSame(weekStart, 'day')
      ) || attendanceWeeks[0],
    [attendanceWeeks, weekStart]
  );
  const [weekRange, setWeekRange] = useState(() =>
    weekRangeFor(
      (() => {
        const year = new Date().getFullYear();
        const weeks = buildAttendanceWeeks(year - 1, year + 1);
        const current = weeks.find((week) =>
          moment(week.weekStartDate).startOf('day').isSame(startOfWeek(new Date()), 'day')
        );
        return current?.weekNum || 1;
      })()
    )
  );
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [draftEmployee, setDraftEmployee] = useState<string[]>([]);
  const [draftDepartment, setDraftDepartment] = useState('all');
  const [draftProject, setDraftProject] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState<string[]>([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [myDays, setMyDays] = useState<DailyAttendance[]>([]);
  const [gridRows, setGridRows] = useState<
    {
      id: string;
      name: string;
      department: string;
      employeeNumber: string;
      role: string;
      days: DailyAttendance[];
    }[]
  >([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [emptyMessage, setEmptyMessage] = useState('');
  const [noRecords, setNoRecords] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [importError, setImportError] = useState<AttendanceApiError | null>(null);
  const [importFileName, setImportFileName] = useState('');
  const [pmStatus, setPmStatus] = useState<PmProjectStatus>('idle');
  const [pmReadyFor, setPmReadyFor] = useState('');
  const [pmReloadKey, setPmReloadKey] = useState(0);
  const draftProjectRef = useRef(draftProject);
  draftProjectRef.current = draftProject;
  const employeeFilterRef = useRef(employeeFilter);
  employeeFilterRef.current = employeeFilter;

  const departmentOptions = isSuperAdmin || isHr ? departments : RM_PM_DEPARTMENTS;

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const mail = storedUser?.Email || storedUser?.email || appUser?.email;
    if (!mail) {
      setCanViewEmployeeAttendance(false);
      setCanImportAttendance(false);
      setCanDownloadAttendance(false);
      return;
    }
    getRolePermissions(mail)
      .then((res) => {
        const tabs = validateMultiRolesAndPermissions(res);
        const permissions = (tabs || [])
          .filter((item) => String(item?.name || '').trim().toLowerCase() === 'attendance')
          .flatMap((item) => item?.permissions || []);
        const hasPermission = (name: string) =>
          permissions.some(
            (permission) =>
              permission?.value === true &&
              String(permission?.name || '').trim().toLowerCase() === name
          );
        setCanViewEmployeeAttendance(hasPermission('view employee attendance'));
        setCanImportAttendance(hasPermission('import attendance'));
        setCanDownloadAttendance(hasPermission('download attendance report'));
        setCanViewEmployeeMapping(hasPermission('view employee mapping'));
        setCanAddEmployeeMapping(hasPermission('add employee mapping'));
        setCanEditEmployeeMapping(hasPermission('edit employee mapping'));
        setCanRemoveEmployeeMapping(hasPermission('remove employee mapping'));
      })
      .catch(() => {
        setCanViewEmployeeAttendance(false);
        setCanImportAttendance(false);
        setCanDownloadAttendance(false);
        setCanViewEmployeeMapping(false);
        setCanAddEmployeeMapping(false);
        setCanEditEmployeeMapping(false);
        setCanRemoveEmployeeMapping(false);
      });
  }, [appUser?.email]);

  useEffect(() => {
    if (!canViewEmployeeAttendance && tab === 'employees') {
      setTab('mine');
    }
    if (!showEmployeeMapping && tab === 'mapping') {
      setTab('mine');
    }
  }, [canViewEmployeeAttendance, showEmployeeMapping, tab]);

  useEffect(() => {
    getAllEmployees()
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setEmployees(list.map(toDirectoryEmployee).filter((employee) => employee.id));
      })
      .catch(() => setEmployees([]));

    if (isSuperAdmin || isHr) {
      getAllDepartments()
        .then((response) => {
          const list = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
            ? response
            : [];
          setDepartments(
            list.map((item) => item?.name || item?.departmentName || '').filter(Boolean)
          );
        })
        .catch(() => setDepartments([]));
    }

    const toOpenProjects = (res: { data?: unknown }) => {
      const list = Array.isArray(res?.data) ? res.data : [];
      return list
        .filter((project) => !project.isClosed)
        .map(toCatalogProject)
        .filter((project): project is ProjectCatalogItem => Boolean(project));
    };

    if (isProjectManager || (!isSuperAdmin && !isHr && !isProjectManagerOrLead)) {
      return;
    }

    getAllProjects()
      .then((res) => setProjects(toOpenProjects(res)))
      .catch(() => {
        if (isSuperAdmin || isHr) {
          setProjects([]);
          return;
        }
        getAllProjectsInternal()
          .then((res) => setProjects(toOpenProjects(res)))
          .catch(() => setProjects([]));
      });
  }, [isSuperAdmin, isHr, isProjectManager, isProjectManagerOrLead]);

  useEffect(() => {
    if (!usePmProjects) {
      return;
    }
    const fromDate = weekStart.format('YYYY-MM-DD');
    const toDate = weekStart.clone().add(6, 'days').format('YYYY-MM-DD');
    let cancelled = false;
    setPmStatus('loading');

    getProjectsManagedBy(currentEmployeeId, fromDate, toDate)
      .then((res) => {
        if (cancelled) {
          return undefined;
        }
        const list: ProjectOption[] = (Array.isArray(res?.data) ? res.data : [])
          .map((project) => ({
            code: String(project?.projectCode || project?.projectKey || '').trim(),
            id: String(project?.projectId || '').trim(),
            name: String(project?.projectName || '').trim()
          }))
          .filter((project) => project.code && project.name);
        setProjects(list);

        if (!list.length) {
          setDraftProject('all');
          setDraftEmployee([]);
          setTeamMembers([]);
          setEmployeeFilter([]);
          setPmStatus('none');
          return undefined;
        }

        const sameProject = list.find((project) => project.code === draftProjectRef.current);
        const selected = sameProject || list[0];
        setDraftProject(selected.code);
        return getTeams(selected.code).then((teamRes) => {
          if (cancelled) {
            return;
          }
          const members = teamMembersFromRoles(Array.isArray(teamRes?.data) ? teamRes.data : []);
          setTeamMembers(members);
          if (!sameProject || employeeFilterRef.current.length === 0) {
            setDraftEmployee([]);
            setEmployeeFilter(members.map((member) => member.id).filter(Boolean));
            setPage(0);
          }
          setPmReadyFor(fromDate);
          setPmStatus('ready');
        });
      })
      .catch(() => {
        if (!cancelled) {
          setProjects([]);
          setTeamMembers([]);
          setEmployeeFilter([]);
          setPmStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [usePmProjects, currentEmployeeId, weekStart, pmReloadKey]);

  useEffect(() => {
    if (!selectedAttendanceWeek) {
      return;
    }
    setWeekRange(weekRangeFor(selectedAttendanceWeek.weekNum));
  }, [selectedAttendanceWeek]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(0);
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fromDate = weekStart.format('YYYY-MM-DD');
    const toDate = weekStart.clone().add(6, 'days').format('YYYY-MM-DD');
    if (tab === 'mapping') {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setNoRecords(false);

    if (tab === 'mine') {
      getMyAttendance(fromDate, toDate)
        .then((res) => {
          if (cancelled) {
            return;
          }
          const data = res?.data?.data;
          const days = Array.isArray(data?.days) ? data.days : [];
          setMyDays(days);
          setEmptyMessage(data?.emptyMessage || '');
          setNoRecords(Boolean(data?.empty) || (days.length > 0 && days.every((day) => !dayHasRecord(day))));
        })
        .catch((error) => {
          if (cancelled) {
            return;
          }
          setMyDays([]);
          setEmptyMessage('');
          setNoRecords(false);
          setLoadError(attendanceErrorMessage(error, 'Unable to load attendance.'));
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    if (usePmProjects && (pmStatus !== 'ready' || pmReadyFor !== fromDate || !employeeFilter.length)) {
      setGridRows([]);
      setTotalCount(0);
      setNoRecords(false);
      setLoading(pmStatus === 'loading' || (pmStatus === 'ready' && pmReadyFor !== fromDate));
      setEmptyMessage(
        pmStatus === 'none'
          ? NO_PM_PROJECTS_MESSAGE
          : pmStatus === 'error'
          ? 'Unable to load your projects.'
          : 'No employees in the selected project match the filters.'
      );
      return undefined;
    }

    getAttendanceGrid({
      fromDate,
      toDate,
      employees: employeeFilter.length ? employeeFilter : undefined,
      departments: departmentFilter === 'all' ? undefined : [departmentFilter],
      search: debouncedSearch || undefined,
      pageNo: page + 1,
      pageSize
    })
      .then((res) => {
        if (cancelled) {
          return;
        }
        const data = res?.data?.data;
        const rows = Array.isArray(data?.employees) ? data.employees : [];
        setGridRows(
          rows.map((employee) => ({
            id: String(employee.employeeId || ''),
            name: employee.name || '-',
            department: employee.department || '-',
            employeeNumber: String(employee.employeeNumber || ''),
            role: employee.role || '',
            days: Array.isArray(employee.days) ? employee.days : []
          }))
        );
        const total = Number(data?.totalCount) || 0;
        setTotalCount(total);
        setEmptyMessage(data?.emptyMessage || '');
        setNoRecords(
          rows.length > 0 &&
            rows.every((employee) => (employee.days || []).every((day) => !dayHasRecord(day)))
        );
        if (
          !isSuperAdmin &&
          !isHr &&
          !isProjectManagerOrLead &&
          Array.isArray(data?.projects) &&
          data.projects.length
        ) {
          setProjects(
            data.projects
              .map((project) => ({
                code: String(project.projectId || ''),
                id: String(project.projectId || ''),
                name: project.projectName || String(project.projectId || '')
              }))
              .filter((project) => project.code && project.name)
          );
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setGridRows([]);
        setTotalCount(0);
        setEmptyMessage('');
        setNoRecords(false);
        setLoadError(attendanceErrorMessage(error, 'Unable to load attendance.'));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    tab,
    weekStart,
    debouncedSearch,
    employeeFilter,
    departmentFilter,
    page,
    pageSize,
    isSuperAdmin,
    isHr,
    isProjectManagerOrLead,
    reloadKey,
    usePmProjects,
    pmStatus,
    pmReadyFor
  ]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => weekStart.clone().add(index, 'days')),
    [weekStart]
  );

  const myAttendance = useMemo(() => {
    const currentId = String(appUser?.EmployeeId || '');
    const match = employees.find((employee) => employee.id === currentId);
    if (match) {
      return [match];
    }
    if (!currentId && !appUser?.Name) {
      return [];
    }
    return [
      {
        id: currentId || '-',
        name: appUser?.Name || 'My attendance',
        department: '-',
        employeeNumber: '',
        role: ''
      }
    ];
  }, [employees, appUser?.EmployeeId, appUser?.Name]);

  const employeeOptions = useMemo(() => {
    const source =
      draftProject !== 'all' ? employeesForTeam(teamMembers, employees) : employees;
    return source.filter((employee) => matchesDepartment(employee, draftDepartment));
  }, [draftProject, teamMembers, employees, draftDepartment]);

  const tableRows = useMemo(() => {
    if (tab === 'mine') {
      return [
        {
          id: String(appUser?.EmployeeId || myAttendance[0]?.id || ''),
          name: appUser?.Name || myAttendance[0]?.name || 'My attendance',
          department: myAttendance[0]?.department && myAttendance[0].department !== '-'
            ? myAttendance[0].department
            : '',
          employeeNumber: myAttendance[0]?.employeeNumber || '',
          role: myAttendance[0]?.role || '',
          days: myDays
        }
      ];
    }
    return gridRows;
  }, [tab, appUser?.EmployeeId, appUser?.Name, myAttendance, myDays, gridRows]);

  const loadProjectEmployees = (projectCode: string) => {
    if (!projectCode || projectCode === 'all') {
      setTeamMembers([]);
      return;
    }

    getTeams(projectCode)
      .then((res) => {
        const roles = Array.isArray(res?.data) ? res.data : [];
        setTeamMembers(teamMembersFromRoles(roles));
      })
      .catch(() => setTeamMembers([]));
  };

  const onProjectChange = (projectCode: string) => {
    setDraftProject(projectCode);
    setDraftEmployee([]);
    loadProjectEmployees(projectCode);
  };

  const applyFilters = () => {
    setPage(0);
    const selectedEmployees =
      draftEmployee.length > 0
        ? draftEmployee
        : draftProject !== 'all'
        ? employeeOptions.map((employee) => employee.id).filter(Boolean)
        : [];
    setEmployeeFilter(selectedEmployees);
    setDepartmentFilter(draftDepartment);
  };

  const showImportMessage = (message: string, severity: 'success' | 'error') => {
    setSnackbarSeverity(severity);
    setSnackBarMessage(message);
    setSnackBarOpen(true);
  };

  const onImportFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !['csv', 'xlsx', 'xls'].includes(extension)) {
      showImportMessage(
        'Attendance file must be an Excel workbook (.xlsx, .xls) or a CSV file.',
        'error'
      );
      return;
    }

    dispatch(AppActions.showLoader());
    setImportFileName(file.name);
    importAttendance(file)
      .then((res) => {
        const result = res?.data?.data;
        const inserted = Number(result?.recordsInserted) || 0;
        const skipped = Number(result?.recordsSkipped) || 0;
        showImportMessage(
          skipped
            ? `Attendance imported. ${inserted} records inserted, ${skipped} already recorded.`
            : `Attendance imported. ${inserted} records inserted.`,
          'success'
        );
        setReloadKey((value) => value + 1);
      })
      .catch((error) => {
        setImportError(attendanceApiError(error, 'Unable to import attendance.'));
      })
      .finally(() => dispatch(AppActions.hideLoader()));
  };

  const clearFilters = () => {
    setPage(0);
    setSearch('');
    setDebouncedSearch('');
    setDraftEmployee([]);
    setDraftDepartment('all');
    setEmployeeFilter([]);
    setDepartmentFilter('all');
    setDraftProject('all');
    setTeamMembers([]);
    setWeekStart(startOfWeek(new Date()));
    if (usePmProjects) {
      setPmStatus('loading');
      setPmReloadKey((value) => value + 1);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#F7F8FC', minHeight: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h3">Attendance</Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {canImportAttendance && (
            <Button
              variant="outlined"
              startIcon={<FileUploadOutlinedIcon />}
              component="label"
            >
              Import Attendance Data
              <input
                hidden
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={onImportFile}
              />
            </Button>
          )}
          {canDownloadAttendance && (
            <Button
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() => setDownloadOpen(true)}
            >
              Download Report
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: (theme) => theme.general.borderRadius,
          border: '1px solid #EEF0F6',
          p: 2.5
        }}
      >
        <Tabs
          value={tab}
          onChange={(_event, value) => setTab(value)}
          sx={{
            mb: 2,
            minHeight: 40,
            '& .MuiTab-root': { textTransform: 'none', minHeight: 40 },
            '& .Mui-selected': { color: 'primary.main' },
            '& .MuiTabs-indicator': { backgroundColor: 'primary.main' }
          }}
        >
          <Tab value="mine" label="My Attendance" />
          {canViewEmployeeAttendance && (
            <Tab value="employees" label="Employee Attendance" />
          )}
          {showEmployeeMapping && <Tab value="mapping" label="Employee Mapping" />}
        </Tabs>

        {tab === 'mapping' ? (
          <EmployeeMapping
            employees={employees}
            canAdd={canAddEmployeeMapping}
            canEdit={canEditEmployeeMapping}
            canRemove={canRemoveEmployeeMapping}
            onMessage={showImportMessage}
          />
        ) : (
        <>
        <Box sx={{ mb: 2 }}>
          <WeeksTab
            weeksList={attendanceWeeks}
            week={selectedAttendanceWeek}
            setWeek={(week) => {
              setPage(0);
              setWeekStart(startOfWeek(week.weekStartDate));
            }}
            weekRange={weekRange}
            setWeekRange={(next) => {
              if (next.from < 0) {
                return;
              }
              const visible = attendanceWeeks.some(
                (_week, index) => index + 3 >= next.from && index + 3 <= next.to
              );
              if (visible) {
                setWeekRange(next);
              }
            }}
          />
        </Box>
        {tab === 'employees' && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          {tab === 'employees' && (
            <TextField
              fullWidth
              size="small"
              placeholder="Search Employee Name or Employee ID..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9AA3B2' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                maxWidth: 420,
                '& .MuiOutlinedInput-root': {
                  borderRadius: (theme) => theme.general.borderRadius,
                  backgroundColor: '#FAFBFE'
                }
              }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {tab === 'employees' && (
            <Button
              color="inherit"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters((open) => !open)}
              sx={{ color: 'text.secondary' }}
            >
              Filters
            </Button>
          )}
        </Box>
        )}

        {tab === 'employees' && showFilters && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto'
              },
              gap: 2,
              alignItems: 'end',
              mb: 2
            }}
          >
            {tab === 'employees' && showProjectFilter && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  Project
                </Typography>
                <SearchableSelect
                  value={draftProject}
                  allLabel="All Projects"
                  includeAll={!usePmProjects}
                  options={projects.map((project) => ({
                    id: project.code,
                    label: project.name
                  }))}
                  onChange={onProjectChange}
                />
              </Box>
            )}
            {tab === 'employees' && showDepartmentFilter && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  Department
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={draftDepartment}
                    onChange={(event) => {
                      setDraftDepartment(event.target.value);
                      setDraftEmployee([]);
                    }}
                  >
                    <MenuItem value="all">All Departments</MenuItem>
                    {departmentOptions.map((department) => (
                      <MenuItem key={department} value={department}>
                        {department}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            {tab === 'employees' && showEmployeeFilter && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  Employee
                </Typography>
                <SearchableSelect
                  multiple
                  value={draftEmployee.filter((id) =>
                    employeeOptions.some((employee) => employee.id === id)
                  )}
                  allLabel="All Employees"
                  options={employeeOptions.map((employee) => ({
                    id: employee.id,
                    label: employee.name
                  }))}
                  onChange={setDraftEmployee}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" onClick={clearFilters} sx={{ color: 'text.secondary' }}>
                Clear
              </Button>
              {tab === 'employees' && (
                <Button variant="contained" onClick={applyFilters}>
                  Filter
                </Button>
              )}
            </Box>
          </Box>
        )}

        {!loading && !loadError && noRecords ? (
          <Box
            sx={{
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                mb: 1.5,
                borderRadius: '50%',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CalendarTodayOutlinedIcon sx={{ color: 'text.secondary' }} />
            </Box>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              No attendance recorded yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are no attendance records available for this period.
            </Typography>
          </Box>
        ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F4F6FB' }}>
                <TableCell sx={{ minWidth: 220, fontSize: 11 }}>EMPLOYEE NAME / ID</TableCell>
                {days.map((day) => (
                  <TableCell
                    key={day.format('YYYY-MM-DD')}
                    align="center"
                    sx={{ minWidth: 130, fontSize: 11 }}
                  >
                    {day.format('dddd').toUpperCase()}
                    <br />
                    {day.format('DD MMM').toUpperCase()}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Loading attendance...
                  </TableCell>
                </TableRow>
              ) : loadError ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'error.main' }}>
                    {loadError}
                  </TableCell>
                </TableRow>
              ) : tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {emptyMessage || 'No employees match the selected week and filters.'}
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((employee) => {
                  const dayByDate = new Map(
                    (employee.days || []).map((record) => [String(record.date).slice(0, 10), record])
                  );
                  return (
                    <TableRow key={employee.id || employee.name} hover>
                      <TableCell>
                        <Typography variant="h5">{employee.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {[employee.employeeNumber, employee.role].filter(Boolean).join(' · ')}
                        </Typography>
                      </TableCell>
                      {days.map((day) => {
                        const record = dayByDate.get(day.format('YYYY-MM-DD'));
                        const label = attendanceCellLabel(record);
                        const time = record?.inTime || '';
                        const detail = time && label.startsWith(time) ? label.slice(time.length).trim() : '';
                        const badge = leaveBadge(record);
                        const shortLeave = isShortLeave(record);
                        const halfDay = Boolean(record?.halfDay);
                        const stackedLeave = shortLeave || halfDay;
                        return (
                          <TableCell
                            key={day.format('YYYY-MM-DD')}
                            align="center"
                            sx={{
                              color: stackedLeave
                                ? record?.late
                                  ? 'error.main'
                                  : 'text.secondary'
                                : badge
                                ? 'inherit'
                                : record?.late
                                ? 'error.main'
                                : record?.onLeave
                                ? 'warning.main'
                                : 'text.secondary'
                            }}
                          >
                            {record?.holiday ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  width: '100%',
                                  minWidth: 0
                                }}
                              >
                                {time && (
                                  <Typography
                                    variant="h5"
                                    component="span"
                                    sx={{
                                      color: record?.late ? 'error.main' : 'text.secondary',
                                      fontSize: 13
                                    }}
                                  >
                                    {time}
                                  </Typography>
                                )}
                                <OverflowText
                                  text={holidayTypeLabel(record)}
                                  tooltip={holidayTooltip(record)}
                                  color={holidayTypeColor(record?.holidayType)}
                                  badge
                                />
                              </Box>
                            ) : stackedLeave ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {time && (
                                  <Typography
                                    variant="h5"
                                    component="span"
                                    sx={{ color: 'inherit', fontSize: 13 }}
                                  >
                                    {time}
                                  </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary">
                                  {shortLeave ? (
                                    shortLeaveLabel(record)
                                  ) : (
                                    <>
                                      {halfDayBase(record)}
                                      {sessionText(record) && (
                                        <Box component="span" sx={{ display: 'block' }}>
                                          {sessionText(record)}
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </Typography>
                              </Box>
                            ) : badge ? (
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1.25,
                                  py: 0.4,
                                  borderRadius: '4px',
                                  border: '1px solid',
                                  borderColor: badge.border,
                                  color: badge.text,
                                  backgroundColor: `${badge.border}24`,
                                  fontSize: 12,
                                  fontWeight: 500,
                                  lineHeight: 1.4,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {badge.label}
                              </Box>
                            ) : time ? (
                              <>
                                <Typography
                                  variant="h5"
                                  component="span"
                                  sx={{ color: 'inherit', fontSize: 13 }}
                                >
                                  {time}
                                </Typography>
                                {detail ? ` ${detail}` : ''}
                              </>
                            ) : (
                              label
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {tab === 'employees' && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => {
                setPageSize(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </Box>
        )}
        </>
        )}
      </Box>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
      <ImportErrorDialog
        fileName={importFileName}
        error={importError}
        onClose={() => setImportError(null)}
      />
      {downloadOpen && (
        <DownloadReportDialog
          open
          employees={employees}
          projects={projects}
          departmentOptions={departmentOptions}
          showEmployeeFilter={showEmployeeFilter}
          showDepartmentFilter={showDepartmentFilter}
          showProjectFilter={showProjectFilter}
          onClose={() => setDownloadOpen(false)}
        />
      )}
    </Box>
  );
};

export default Attendance;
